import { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Button,
  IconButton,
  TextField,
  Typography,
  Stack,
  CircularProgress,
  Alert,
  useTheme,
  Switch,
  MenuItem,
  Tooltip,
  Chip,
  Paper,
  Fade,
  Slide,
} from "@mui/material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import RefreshIcon from "@mui/icons-material/Refresh";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import ViewSidebarIcon from "@mui/icons-material/ViewSidebar";
import ViewAgendaIcon from "@mui/icons-material/ViewAgenda";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import RateReviewIcon from "@mui/icons-material/RateReview";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import EventIcon from "@mui/icons-material/Event";
import StorefrontIcon from "@mui/icons-material/Storefront";
import axios from "axios";
import { tokens } from "../../../../../../theme";
import type { BookingData } from "../../../../types/view-booking";
import { useShareTemplates } from "../../../../../configurations/share-messages/hooks/useShareMessageTemplates";
import { showSuccessToast, showErrorToast } from "../../../../../../components/common/ShowToast";
import apiService from "../../../../../../service/Api";

interface AIWhatsAppDrawerProps {
  open: boolean;
  onClose: () => void;
  bookingData?: BookingData | null;
  currentUserName: string;
  initialMode?: "review" | "thankyou" | "anniversary" | null;
}

interface ChatMessage {
  id: string;
  sender: "user" | "model";
  text: string;
  timestamp: Date;
  clientMessage?: string;
  explanation?: string;
  isTemplate?: boolean;
}

const normalizePhoneForWhatsapp = (phone?: string) => {
  if (!phone) return "";
  let cleaned = phone.replace(/[^\d+]/g, "");
  cleaned = cleaned.replace(/^\+/, "");
  if (cleaned.startsWith("0")) {
    cleaned = `263${cleaned.slice(1)}`;
  }
  return cleaned;
};

const categoryMap: Record<string, string> = {
  Funeral: "funeral",
  Conference: "conference",
  Church: "church",
};

const getBookingPageContext = (bookingData?: BookingData | null): string => {
  if (!bookingData) return "No booking details available.";

  const beloved = bookingData.beloved;
  const organiser = bookingData.organiser;

  const streamInfo = bookingData.streams?.map((s, idx) => `
Stream #${idx + 1}:
- Date: ${s.date || "N/A"}
- Time: ${s.start || "N/A"} to ${s.end || "N/A"}
- Venue: ${s.venueDetails?.name || s.addressOne || "N/A"}
- Stream Link: ${s.website_stream_link || "N/A"}
- Template ID: ${s.template_id || "N/A"}
  `.trim()).join("\n\n") || "No stream details scheduled.";

  const serviceInfo = bookingData.services?.map((s, idx) => `
Service #${idx + 1}:
- Venue Name: ${s.venue?.name || "N/A"}
- Venue Address: ${s.venue?.address_one || "N/A"}
- Notes: ${s.service_notes || "N/A"}
  `.trim()).join("\n\n") || "No service details scheduled.";

  const contactsInfo = bookingData.contacts?.map((c, idx) => `
Contact #${idx + 1}:
- Name: ${c.name || "N/A"}
- Phone: ${c.phone || "N/A"}
- Email: ${c.email || "N/A"}
- Role: ${c.role || "N/A"}
  `.trim()).join("\n\n") || "No additional contacts listed.";

  return `
=== BOOKING BASIC INFO ===
Booking ID: ${bookingData.id || "N/A"}
Reference Number: ${bookingData.reference_number || "N/A"}
Service Type: ${bookingData.service || "N/A"}
Status: ${bookingData.status || "N/A"}
Completeness Score: ${bookingData.completeness_score || 0}%

=== CLIENT / ORGANISER ===
Name: ${organiser?.name || "N/A"}
Phone: ${organiser?.phone || "N/A"}
Email: ${organiser?.email || "N/A"}
Organiser Notes: ${organiser?.notes || "N/A"}
Organiser Special Notes: ${organiser?.specialNotes || "N/A"}

=== DECEASED DETAILS ===
Name: ${beloved?.name || "N/A"}
Gender: ${beloved?.details?.gender || "N/A"}
Date of Birth: ${beloved?.details?.dateOfBirth || "N/A"}
Date of Death: ${beloved?.details?.dateOfDeath || "N/A"}
Religion: ${beloved?.details?.religion || "N/A"}
Denomination: ${beloved?.details?.denomination || "N/A"}

=== STREAMS ===
${streamInfo}

=== SERVICES ===
${serviceInfo}

=== ADDITIONAL CONTACTS ===
${contactsInfo}
  `.trim();
};

const parseAIResponse = (text: string) => {
  const match = text.match(/<client_message>([\s\S]*?)<\/client_message>/i);
  if (match) {
    const clientMessage = match[1].trim();
    const explanation = text.replace(/<client_message>[\s\S]*?<\/client_message>/i, "").trim();
    return {
      explanation: explanation || "Here is the drafted message for the client:",
      clientMessage: clientMessage,
    };
  }
  return {
    explanation: text,
    clientMessage: undefined,
  };
};

const getModeFromType = (type: string): "review" | "thankyou" | "anniversary" => {
  if (type === "client_review") return "review";
  if (type === "thank_you_message") return "thankyou";
  return "anniversary";
};

interface SendWhatsAppResponse {
  ok: boolean;
  message?: string;
  error?: string;
}

export default function AIWhatsAppDrawer({
  open,
  onClose,
  bookingData,
  currentUserName,
  initialMode = null,
}: AIWhatsAppDrawerProps) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [layoutMode, setLayoutMode] = useState<"bottomBar" | "sidePanel">("sidePanel");
  const [isMinimized, setIsMinimized] = useState(false);
  const [, setMode] = useState<"review" | "thankyou" | "anniversary" | null>(initialMode);
  
  // Chat state
  const [chatHistory, setChatHistory] = useState<ChatMessage[]>([]);
  const [inputPrompt, setInputPrompt] = useState("");
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>("");
  const [includeClientName, setIncludeClientName] = useState(true);

  const { templates, fetchTemplates } = useShareTemplates();
  const chatEndRef = useRef<HTMLDivElement | null>(null);

  const clientName = bookingData?.organiser?.name || "Client";
  const clientFirstName = clientName.trim().split(/\s+/)[0];
  const service = bookingData?.service || "Memorial Service";
  const referenceNumber = bookingData?.reference_number ?? bookingData?.id ?? "";
  const reviewLink = `${window.location.origin}/client-review/${referenceNumber}`;

  const rawDate = bookingData?.streams?.[0]?.date;
  const serviceDate = rawDate
    ? (() => {
        const [day, month, year] = rawDate.split("-");
        return new Date(
          Number(year),
          Number(month) - 1,
          Number(day),
        ).toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "long",
          year: "numeric",
        });
      })()
    : "N/A";

  const compileTemplate = useCallback(
    (body: string, modeType: "review" | "thankyou" | "anniversary", withName: boolean = true) => {
      const cName =
        modeType === "review"
          ? withName
            ? clientName
            : "there"
          : modeType === "anniversary"
          ? bookingData?.organiser?.name || "Family"
          : clientName;

      const values: Record<string, string> = {
        clientName: cName,
        deceasedName:
          bookingData?.beloved?.name && bookingData.beloved.name !== "N/A"
            ? bookingData.beloved.name
            : "your loved one",
        funeralHomeName: bookingData?.services?.[0]?.venue?.name ?? "N/A",
        companyName: "Muzukuru",
        bookingReference: String(bookingData?.reference_number ?? bookingData?.id ?? "N/A"),
        eventName: service,
        date: serviceDate,
        time: bookingData?.streams?.[0]?.start ?? "N/A",
        venue: bookingData?.services?.[0]?.venue?.name ?? "N/A",
        address: bookingData?.services?.[0]?.venue?.address_one ?? "N/A",
        website_stream_link: bookingData?.streams?.[0]?.website_stream_link ?? reviewLink,
      };

      let filled = body;
      Object.entries(values).forEach(([key, val]) => {
        filled = filled.replaceAll(`{${key}}`, val);
      });
      return filled;
    },
    [bookingData, clientName, service, serviceDate, reviewLink]
  );

  // Auto-scroll chat history
  useEffect(() => {
    if (chatHistory.length > 0) {
      chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [chatHistory]);

  // Load templates on open
  useEffect(() => {
    if (open && bookingData) {
      const category = categoryMap[bookingData.service ?? ""] ?? "funeral";
      fetchTemplates(category);
    }
  }, [open, bookingData, fetchTemplates]);

  // Reset states on drawer close
  useEffect(() => {
    if (!open) {
      setMode(null);
      setChatHistory([]);
      setInputPrompt("");
      setError("");
      setSelectedTemplateId("");
      setIncludeClientName(true);
      setIsMinimized(false);
    }
  }, [open]);

  // Handle deep-link triggers from BookingsSidebar
  useEffect(() => {
    if (open && initialMode) {
      setMode(initialMode);
      setIsMinimized(false);
      setChatHistory([]);
      triggerAutoDraft(initialMode);
    }
  }, [open, initialMode]);

  const triggerAutoDraft = async (modeType: "review" | "thankyou" | "anniversary") => {
    setLoading(true);
    setError("");

    const displayLabel = modeType === "review" 
      ? "Request Client Review" 
      : modeType === "thankyou" 
      ? "Thank You Message" 
      : "Anniversary Memorial Message";

    const userMsg: ChatMessage = {
      id: Math.random().toString(),
      sender: "user",
      text: `Draft a ${displayLabel} for the client.`,
      timestamp: new Date(),
    };

    setChatHistory([userMsg]);

    const apiKey =
      process.env.REACT_APP_GEMINI_API_KEY ||
      process.env.GEMINI_API_KEY ||
      (window as unknown as { GEMINI_API_KEY?: string }).GEMINI_API_KEY;

    if (!apiKey) {
      setError("AI feature is currently unavailable because the API key is not configured.");
      setLoading(false);
      return;
    }

    const deceasedName =
      bookingData?.beloved?.name && bookingData.beloved.name !== "N/A"
        ? bookingData.beloved.name
        : "";

    let modePrompt = "";
    if (modeType === "review") {
      modePrompt = `Write a polite, warm, and professional WhatsApp message to client ${clientFirstName} requesting them to review their experience with Muzukuru Funeral Streaming for the ${service} streaming service ${
        deceasedName ? `of the late ${deceasedName}` : ""
      }.
Please include this review link directly in the message: ${reviewLink}
Identify that you are ${currentUserName} from Muzukuru Funeral Streaming.`;
    } else if (modeType === "thankyou") {
      modePrompt = `Write a warm and sincere thank you WhatsApp message from ${currentUserName} at Muzukuru Funeral Streaming to the client ${clientFirstName} for trusting us to stream the ${service} service ${
        deceasedName ? `for the late ${deceasedName}` : ""
      }. Keep it polite, professional, and empathetic.`;
    } else if (modeType === "anniversary") {
      modePrompt = `Write a comforting, warm, and supportive memorial anniversary WhatsApp message from Muzukuru Funeral Streaming to the client ${clientFirstName} remembering the late ${
        deceasedName || "loved one"
      } on their anniversary. Keep it respectful, gentle, and comforting.`;
    }

    const bookingContext = getBookingPageContext(bookingData);
    const systemPrompt = `You are a helpful, respectful, and intelligent AI Assistant for Muzukuru staff.
You have access to the context of the current booking details page.

Here is the context of the page:
${bookingContext}

Your tasks:
1. Answer questions about the booking details page accurately using the context provided.
2. Help draft WhatsApp/SMS messages to the client (organiser) or family.

IMPORTANT INSTRUCTION FOR MESSAGES TO CLIENTS:
When drafting a message intended to be sent to the client, you must wrap the message body inside the <client_message> and </client_message> tags.
Do not put the explanation or introduction inside these tags, only the exact text to be copied/sent to the client.
For example:
"Sure, here is a thank you message for the client:
<client_message>Dear ${clientFirstName}, thank you for choosing Muzukuru. We appreciate your trust...</client_message>"

If the user is asking a general question about the page details, do NOT use the <client_message> tags. Just reply in clean markdown.

Staff Member (Your Name): ${currentUserName}
Client Name: ${clientName}
`;

    try {
      const response = await axios.post(
        `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash:generateContent?key=${apiKey}`,
        {
          contents: [
            {
              role: "user",
              parts: [{ text: modePrompt }],
            },
          ],
          systemInstruction: {
            parts: [{ text: systemPrompt }],
          },
        }
      );

      const generated = response.data?.candidates?.[0]?.content?.parts?.[0]?.text || "";
      const { explanation, clientMessage } = parseAIResponse(generated);

      const aiMsg: ChatMessage = {
        id: Math.random().toString(),
        sender: "model",
        text: generated,
        timestamp: new Date(),
        clientMessage,
        explanation,
      };

      setChatHistory([userMsg, aiMsg]);
    } catch (err: unknown) {
      console.error("API Error", err);
      let errMsg = "An error occurred while communicating with AI.";
      if (axios.isAxiosError(err)) {
        errMsg = err.response?.data?.error?.message || err.message;
      } else if (err instanceof Error) {
        errMsg = err.message;
      }
      setError(errMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async (textToSend: string) => {
    if (!textToSend.trim()) return;

    setError("");
    setLoading(true);

    const userMsg: ChatMessage = {
      id: Math.random().toString(),
      sender: "user",
      text: textToSend,
      timestamp: new Date(),
    };

    const updatedHistory = [...chatHistory, userMsg];
    setChatHistory(updatedHistory);
    setInputPrompt("");

    const apiKey =
      process.env.REACT_APP_GEMINI_API_KEY ||
      process.env.GEMINI_API_KEY ||
      (window as unknown as { GEMINI_API_KEY?: string }).GEMINI_API_KEY;

    if (!apiKey) {
      setError("AI feature is currently unavailable because the API key is not configured.");
      setLoading(false);
      return;
    }

    const bookingContext = getBookingPageContext(bookingData);
    const systemPrompt = `You are a helpful, respectful, and intelligent AI Assistant for Muzukuru staff.
You have access to the context of the current booking details page.

Here is the context of the page:
${bookingContext}

Your tasks:
1. Answer questions about the booking details page accurately using the context provided.
2. Help draft WhatsApp/SMS messages to the client (organiser) or family.

IMPORTANT INSTRUCTION FOR MESSAGES TO CLIENTS:
When drafting a message intended to be sent to the client, you must wrap the message body inside the <client_message> and </client_message> tags.
Do not put the explanation or introduction inside these tags, only the exact text to be copied/sent to the client.
For example:
"Sure, here is a thank you message for the client:
<client_message>Dear ${clientFirstName}, thank you for choosing Muzukuru. We appreciate your trust...</client_message>"

If the user is asking a general question about the page details, do NOT use the <client_message> tags. Just reply in clean markdown.

Staff Member (Your Name): ${currentUserName}
Client Name: ${clientName}
`;

    const contents = updatedHistory.map((msg) => ({
      role: msg.sender,
      parts: [{ text: msg.text }],
    }));

    try {
      const response = await axios.post(
        `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash:generateContent?key=${apiKey}`,
        {
          contents,
          systemInstruction: {
            parts: [{ text: systemPrompt }],
          },
        }
      );

      const generated = response.data?.candidates?.[0]?.content?.parts?.[0]?.text || "";
      const { explanation, clientMessage } = parseAIResponse(generated);

      const aiMsg: ChatMessage = {
        id: Math.random().toString(),
        sender: "model",
        text: generated,
        timestamp: new Date(),
        clientMessage,
        explanation,
      };

      setChatHistory((prev) => [...prev, aiMsg]);
    } catch (err: unknown) {
      console.error("API Error", err);
      let errMsg = "An error occurred while communicating with AI.";
      if (axios.isAxiosError(err)) {
        errMsg = err.response?.data?.error?.message || err.message;
      } else if (err instanceof Error) {
        errMsg = err.message;
      }
      setError(errMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyTemplate = (templateId: string) => {
    const template = templates.find((t) => t.id === templateId);
    if (!template) return;

    const modeType = getModeFromType(template.template_type);
    const compiledText = compileTemplate(template.body_template, modeType, includeClientName);

    const userMsg: ChatMessage = {
      id: Math.random().toString(),
      sender: "user",
      text: `Apply template: "${template.name}"`,
      timestamp: new Date(),
    };

    const aiMsg: ChatMessage = {
      id: Math.random().toString(),
      sender: "model",
      text: `Applying template: "${template.name}"\n\n<client_message>${compiledText}</client_message>`,
      timestamp: new Date(),
      clientMessage: compiledText,
      explanation: `Here is the compiled "${template.name}" template:`,
      isTemplate: true,
    };

    setChatHistory((prev) => [...prev, userMsg, aiMsg]);
  };

  const handleEditClientMessage = (id: string, newText: string) => {
    setChatHistory((prev) =>
      prev.map((msg) =>
        msg.id === id ? { ...msg, clientMessage: newText } : msg
      )
    );
  };

  const handleCopyMessage = (text: string) => {
    navigator.clipboard.writeText(text);
    showSuccessToast("Message copied to clipboard!");
  };

  const handleOpenWhatsApp = (text: string) => {
    const rawPhone =
      bookingData?.organiser?.phone && bookingData.organiser.phone !== "N/A"
        ? bookingData.organiser.phone
        : "";
    const normalizedPhone = normalizePhoneForWhatsapp(rawPhone);
    const encoded = encodeURIComponent(text || "");
    const url = `https://wa.me/${normalizedPhone}?text=${encoded}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const [sendingBusinessId, setSendingBusinessId] = useState<string | null>(null);

  const handleSendMuzukuruBusinessMessage = async (msgId: string, text: string) => {
    const rawPhone =
      bookingData?.organiser?.phone && bookingData.organiser.phone !== "N/A"
        ? bookingData.organiser.phone
        : "";
    const normalizedPhone = normalizePhoneForWhatsapp(rawPhone);

    if (!normalizedPhone) {
      showErrorToast("Client phone number is missing or invalid.");
      return;
    }

    setSendingBusinessId(msgId);
    try {
      const res = await apiService.post<SendWhatsAppResponse>("/api/whatsapp/send-direct/", {
        recipient: normalizedPhone,
        message: text,
        booking_id: bookingData?.id,
      });
      if (res.data?.ok) {
        showSuccessToast(res.data.message || "Message sent via Muzukuru Business WhatsApp!");
      } else {
        showErrorToast(res.data?.error || "Failed to send message via Muzukuru Business.");
      }
    } catch (err: unknown) {
      console.error("WhatsApp Business API error:", err);
      let errMsg = "Failed to send message via Muzukuru Business.";
      if (axios.isAxiosError(err)) {
        errMsg = err.response?.data?.error || err.message;
      } else if (err instanceof Error) {
        errMsg = err.message;
      }
      showErrorToast(errMsg);
    } finally {
      setSendingBusinessId(null);
    }
  };

  const isFuneral = (bookingData?.service || "").toLowerCase() === "funeral";

  if (!open && !isMinimized) {
    return null;
  }

  // Floating Minimized Button (when user collapses assistant)
  if (isMinimized) {
    return (
      <Box
        sx={{
          position: "fixed",
          bottom: 90,
          right: 24,
          zIndex: 1300,
        }}
      >
        <Paper
          elevation={6}
          onClick={() => setIsMinimized(false)}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            px: 2.5,
            py: 1.5,
            borderRadius: "30px",
            bgcolor: theme.palette.mode === "dark" ? "#1e293b" : "#ffffff",
            color: colors.primary[700],
            cursor: "pointer",
            border: `1.5px solid ${theme.palette.mode === "dark" ? "#334155" : "#e2e8f0"}`,
            boxShadow: "0 10px 25px rgba(0,0,0,0.15)",
            transition: "transform 0.2s ease, boxShadow 0.2s ease",
            "&:hover": {
              transform: "translateY(-2px)",
              boxShadow: "0 14px 30px rgba(0,0,0,0.2)",
              borderColor: "#724E41",
            },
          }}
        >
          <AutoAwesomeIcon sx={{ color: "#724E41", fontSize: 20 }} />
          <Typography fontWeight={700} variant="body2" sx={{ color: colors.primary[700] }}>
            AI Assistant
          </Typography>
          {chatHistory.length > 0 && (
            <Chip
              label={`${chatHistory.length} msgs`}
              size="small"
              sx={{
                bgcolor: "#724E41",
                color: "#fff",
                fontSize: "10px",
                height: 20,
                fontWeight: 600,
              }}
            />
          )}
        </Paper>
      </Box>
    );
  }

  const renderChatArea = (maxHeight?: string) => {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%", maxHeight: maxHeight || "none" }}>
        {/* Templates Selector Row */}
        <Box sx={{ mb: 1.5, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
          {templates.length > 0 && (
            <TextField
              select
              size="small"
              label="Use Predefined Template"
              value={selectedTemplateId}
              onChange={(e) => {
                const val = e.target.value;
                setSelectedTemplateId(val);
                handleApplyTemplate(val);
              }}
              sx={{
                minWidth: "180px",
                "& .MuiOutlinedInput-root": { borderRadius: "10px", fontSize: "12px" },
                "& .MuiInputLabel-root": { fontSize: "12px" },
              }}
            >
              <MenuItem value="" disabled>
                Select template...
              </MenuItem>
              {templates.map((t) => (
                <MenuItem key={t.id} value={t.id} sx={{ fontSize: "12px" }}>
                  {t.name}
                </MenuItem>
              ))}
            </TextField>
          )}
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Switch
              checked={includeClientName}
              onChange={(e) => setIncludeClientName(e.target.checked)}
              size="small"
              sx={{
                "& .MuiSwitch-thumb": { bgcolor: "background.paper" },
                "& .Mui-checked + .MuiSwitch-track": {
                  bgcolor: "#724E41 !important",
                },
              }}
            />
            <Typography sx={{ fontSize: "11px", color: "#64748b" }}>
              Include Client Name
            </Typography>
          </Stack>
        </Box>

        {/* Scrollable Chat History */}
        <Box sx={{ flex: 1, overflowY: "auto", pr: 0.5, mb: 1 }}>
          {chatHistory.length === 0 ? (
            /* Suggestion and Welcome State */
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                py: 4,
                px: 1,
              }}
            >
              <AutoAwesomeIcon sx={{ color: "#724E41", fontSize: 32, mb: 1.5 }} />
              <Typography
                variant="h6"
                fontWeight={800}
                sx={{
                  background: "linear-gradient(45deg, #724E41 30%, #D1A077 90%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  mb: 1,
                  fontSize: "16px",
                }}
              >
                Muzukuru AI Assist
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "#64748b", maxWidth: "260px", mb: 3, fontSize: "12px", lineHeight: 1.4 }}
              >
                Ask me questions about the details of this booking page, or draft a message to the client.
              </Typography>

              <Stack spacing={1.2} sx={{ width: "100%" }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => handleSendMessage("Draft a WhatsApp message to client requesting a review of their booking experience.")}
                  startIcon={<RateReviewIcon sx={{ color: "#724E41", fontSize: 14 }} />}
                  sx={{
                    justifyContent: "flex-start",
                    textTransform: "none",
                    py: 1,
                    px: 1.5,
                    borderRadius: "10px",
                    fontSize: "12px",
                    color: colors.primary[700],
                    borderColor: theme.palette.mode === "dark" ? "#334155" : "#e2e8f0",
                    "&:hover": { bgcolor: "action.hover", borderColor: "#724E41" },
                  }}
                >
                  Request Client Review
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => handleSendMessage("Draft a WhatsApp thank you message to the client for choosing Muzukuru.")}
                  startIcon={<FavoriteBorderIcon sx={{ color: "#724E41", fontSize: 14 }} />}
                  sx={{
                    justifyContent: "flex-start",
                    textTransform: "none",
                    py: 1,
                    px: 1.5,
                    borderRadius: "10px",
                    fontSize: "12px",
                    color: colors.primary[700],
                    borderColor: theme.palette.mode === "dark" ? "#334155" : "#e2e8f0",
                    "&:hover": { bgcolor: "action.hover", borderColor: "#724E41" },
                  }}
                >
                  Send Thank You Message
                </Button>
                {isFuneral && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => handleSendMessage("Draft a comforting WhatsApp message to the family for the anniversary memorial.")}
                    startIcon={<EventIcon sx={{ color: "#724E41", fontSize: 14 }} />}
                    sx={{
                      justifyContent: "flex-start",
                      textTransform: "none",
                      py: 1,
                      px: 1.5,
                      borderRadius: "10px",
                      fontSize: "12px",
                      color: colors.primary[700],
                      borderColor: theme.palette.mode === "dark" ? "#334155" : "#e2e8f0",
                      "&:hover": { bgcolor: "action.hover", borderColor: "#724E41" },
                    }}
                  >
                    Anniversary Memorial Message
                  </Button>
                )}
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => handleSendMessage("Summarize the details of this booking.")}
                  startIcon={<ViewAgendaIcon sx={{ color: "#724E41", fontSize: 14 }} />}
                  sx={{
                    justifyContent: "flex-start",
                    textTransform: "none",
                    py: 1,
                    px: 1.5,
                    borderRadius: "10px",
                    fontSize: "12px",
                    color: colors.primary[700],
                    borderColor: theme.palette.mode === "dark" ? "#334155" : "#e2e8f0",
                    "&:hover": { bgcolor: "action.hover", borderColor: "#724E41" },
                  }}
                >
                  Summarize Booking Details
                </Button>
              </Stack>
            </Box>
          ) : (
            chatHistory.map((msg) => {
              const isUser = msg.sender === "user";
              return (
                <Box
                  key={msg.id}
                  sx={{
                    display: "flex",
                    justifyContent: isUser ? "flex-end" : "flex-start",
                    mb: 2,
                  }}
                >
                  <Stack
                    direction={isUser ? "row-reverse" : "row"}
                    spacing={1}
                    alignItems="flex-start"
                    sx={{ maxWidth: "85%" }}
                  >
                    <IconButton
                      size="small"
                      disabled
                      sx={{
                        bgcolor: isUser ? "#724E41" : "#e2e8f0",
                        color: isUser ? "white !important" : "#724E41 !important",
                        width: 28,
                        height: 28,
                        p: 0,
                        borderRadius: "50%",
                      }}
                    >
                      {isUser ? (
                        <PersonOutlineIcon sx={{ fontSize: 16 }} />
                      ) : (
                        <AutoAwesomeIcon sx={{ fontSize: 14 }} />
                      )}
                    </IconButton>

                    <Stack spacing={0.5} alignItems={isUser ? "flex-end" : "flex-start"}>
                      <Box
                        sx={{
                          bgcolor: isUser ? "#724E41" : theme.palette.mode === "dark" ? "#334155" : "#f1f5f9",
                          color: isUser ? "white" : colors.primary[700],
                          borderRadius: isUser ? "16px 16px 2px 16px" : "16px 16px 16px 2px",
                          p: 1.5,
                          fontSize: "12.5px",
                          lineHeight: 1.5,
                          whiteSpace: "pre-wrap",
                          boxShadow: "0 2px 5px rgba(0,0,0,0.05)",
                        }}
                      >
                        {!isUser && msg.clientMessage ? (
                          <Stack spacing={1}>
                            {msg.explanation && (
                              <Typography variant="body2" sx={{ fontSize: "12.5px", color: "inherit", mb: 0.5 }}>
                                {msg.explanation}
                              </Typography>
                            )}
                            <Box
                              sx={{
                                border: `1.5px solid ${theme.palette.mode === "dark" ? "#1b4d3e" : "#8ce0b0"}`,
                                borderRadius: "12px",
                                bgcolor: theme.palette.mode === "dark" ? "#0a2618" : "#f0fdf4",
                                p: 1.5,
                                mt: 1,
                              }}
                            >
                              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                                <WhatsAppIcon sx={{ color: "#25D366", fontSize: 16 }} />
                                <Typography variant="caption" sx={{ color: "#25D366", fontWeight: 700, fontSize: "10px", textTransform: "uppercase" }}>
                                  WhatsApp Message Draft
                                </Typography>
                              </Stack>
                              
                              <TextField
                                multiline
                                fullWidth
                                variant="standard"
                                value={msg.clientMessage}
                                onChange={(e) => handleEditClientMessage(msg.id, e.target.value)}
                                InputProps={{
                                  disableUnderline: true,
                                  sx: {
                                    fontSize: "12px",
                                    lineHeight: 1.5,
                                    fontFamily: "inherit",
                                    color: theme.palette.mode === "dark" ? "#e2e8f0" : "#0f172a",
                                  },
                                }}
                              />

                              <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center" sx={{ mt: 1.5, pt: 1, borderTop: "1px solid rgba(0, 0, 0, 0.08)" }}>
                                <Tooltip title="Copy message">
                                  <IconButton size="small" onClick={() => handleCopyMessage(msg.clientMessage || "")}>
                                    <ContentCopyIcon fontSize="small" sx={{ fontSize: 14 }} />
                                  </IconButton>
                                </Tooltip>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  disabled={sendingBusinessId === msg.id}
                                  onClick={() => handleSendMuzukuruBusinessMessage(msg.id, msg.clientMessage || "")}
                                  startIcon={
                                    sendingBusinessId === msg.id ? (
                                      <CircularProgress size={12} color="inherit" />
                                    ) : (
                                      <StorefrontIcon sx={{ fontSize: 13 }} />
                                    )
                                  }
                                  sx={{
                                    borderColor: "#724E41",
                                    color: theme.palette.mode === "dark" ? "#f59e0b" : "#724E41",
                                    fontSize: "10px",
                                    fontWeight: 600,
                                    textTransform: "none",
                                    py: 0.2,
                                    px: 1,
                                    borderRadius: "6px",
                                    "&:hover": {
                                      borderColor: "#5a3d33",
                                      bgcolor: "rgba(114, 78, 65, 0.08)",
                                    },
                                  }}
                                >
                                  {sendingBusinessId === msg.id ? "Sending..." : "Muzukuru Business"}
                                </Button>
                                <Button
                                  size="small"
                                  variant="contained"
                                  onClick={() => handleOpenWhatsApp(msg.clientMessage || "")}
                                  startIcon={<WhatsAppIcon sx={{ fontSize: 12 }} />}
                                  sx={{
                                    bgcolor: "#25D366",
                                    color: "white",
                                    fontSize: "10px",
                                    fontWeight: 600,
                                    textTransform: "none",
                                    py: 0.2,
                                    px: 1,
                                    borderRadius: "6px",
                                    "&:hover": { bgcolor: "#128C7E" },
                                  }}
                                >
                                  Send WhatsApp
                                </Button>
                              </Stack>
                            </Box>
                          </Stack>
                        ) : (
                          msg.text
                        )}
                      </Box>
                      
                      {!isUser && !msg.clientMessage && (
                        <Stack direction="row" spacing={0.5} sx={{ opacity: 0.6, "&:hover": { opacity: 1 } }}>
                          <Tooltip title="Copy text">
                            <IconButton size="small" onClick={() => handleCopyMessage(msg.text)}>
                              <ContentCopyIcon sx={{ fontSize: 12 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Send via Muzukuru Business WhatsApp">
                            <IconButton
                              size="small"
                              disabled={sendingBusinessId === msg.id}
                              onClick={() => handleSendMuzukuruBusinessMessage(msg.id, msg.text)}
                            >
                              {sendingBusinessId === msg.id ? (
                                <CircularProgress size={12} sx={{ color: "#724E41" }} />
                              ) : (
                                <StorefrontIcon sx={{ fontSize: 12, color: "#724E41" }} />
                              )}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Send whole text to client via WhatsApp">
                            <IconButton size="small" onClick={() => handleOpenWhatsApp(msg.text)}>
                              <WhatsAppIcon sx={{ fontSize: 12, color: "#25D366" }} />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      )}

                      <Typography sx={{ fontSize: "9px", color: "#94a3b8", mt: 0.2 }}>
                        {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </Typography>
                    </Stack>
                  </Stack>
                </Box>
              );
            })
          )}
          {loading && (
            <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 2, pl: 5 }}>
              <CircularProgress size={20} sx={{ color: "#724E41" }} />
            </Box>
          )}
          {error && (
            <Alert severity="error" sx={{ borderRadius: "10px", fontSize: "12px", mb: 2 }}>
              {error}
            </Alert>
          )}
          <div ref={chatEndRef} />
        </Box>
      </Box>
    );
  };

  const renderInputBar = () => {
    return (
      <Box
        sx={{
          display: "flex",
          border: `1px solid ${
            theme.palette.mode === "dark" ? "#4a5568" : "#cbd5e1"
          }`,
          borderRadius: "24px",
          p: "6px 10px 6px 16px",
          bgcolor: theme.palette.mode === "dark" ? "#1e293b" : "#ffffff",
          alignItems: "center",
          gap: 1,
          "&:focus-within": {
            borderColor: "#724E41",
            boxShadow: "0 4px 12px rgba(114, 78, 65, 0.15)",
          },
        }}
      >
        <TextField
          placeholder={
            chatHistory.length > 0
              ? "Ask AI or refine draft..."
              : "Ask AI to write a WhatsApp message or ask a question..."
          }
          fullWidth
          variant="standard"
          value={inputPrompt}
          onChange={(e) => setInputPrompt(e.target.value)}
          disabled={loading}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSendMessage(inputPrompt);
            }
          }}
          InputProps={{
            disableUnderline: true,
            sx: {
              fontSize: "13.5px",
              color: colors.primary[700],
              fontFamily: "inherit",
            },
          }}
        />

        <IconButton
          size="small"
          disabled={loading || !inputPrompt.trim()}
          onClick={() => handleSendMessage(inputPrompt)}
          sx={{
            bgcolor: inputPrompt.trim() ? "#724E41" : "action.disabledBackground",
            color: "white",
            width: 32,
            height: 32,
            "&:hover": {
              bgcolor: "#5d3e33",
            },
            "&.Mui-disabled": {
              color: "rgba(255, 255, 255, 0.3)",
            },
          }}
        >
          {loading ? (
            <CircularProgress size={16} color="inherit" />
          ) : (
            <ArrowUpwardIcon sx={{ fontSize: 18 }} />
          )}
        </IconButton>
      </Box>
    );
  };

  return (
    <Box sx={{ pointerEvents: "none", zIndex: 1300, position: "fixed", inset: 0 }}>
      {/* ---------------------------------------------------- */}
      {/* LAYOUT 1: FLOATING BOTTOM BAR MODE (Google Docs style) */}
      {/* ---------------------------------------------------- */}
      {layoutMode === "bottomBar" && (
        <Box
          sx={{
            position: "fixed",
            bottom: 24,
            left: "50%",
            transform: "translateX(-50%)",
            width: { xs: "92%", sm: 580, md: 650 },
            pointerEvents: "auto",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          {/* Expanded Generated Card (Upward Expansion) */}
          {(chatHistory.length > 0 || loading || !!error) && (
            <Fade in={chatHistory.length > 0 || loading || !!error}>
              <Paper
                elevation={8}
                sx={{
                  width: "100%",
                  maxHeight: "55vh",
                  borderRadius: "20px",
                  p: 2.5,
                  bgcolor: theme.palette.mode === "dark" ? "#1e293b" : "#ffffff",
                  border: `1px solid ${theme.palette.mode === "dark" ? "#334155" : "#e2e8f0"}`,
                  boxShadow: "0 20px 40px rgba(0,0,0,0.18)",
                  backdropFilter: "blur(12px)",
                  display: "flex",
                  flexDirection: "column",
                  mb: 1
                }}
              >
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <AutoAwesomeIcon sx={{ color: "#724E41" }} />
                    <Typography variant="subtitle1" fontWeight={700} sx={{ color: colors.primary[700] }}>
                      Muzukuru AI Assistant
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={0.5}>
                    <Tooltip title="Clear chat history">
                      <IconButton size="small" onClick={() => setChatHistory([])} sx={{ color: colors.primary[300] }}>
                        <RefreshIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Dock to Floating Side Panel">
                      <IconButton
                        size="small"
                        onClick={() => setLayoutMode("sidePanel")}
                        sx={{ color: colors.primary[300] }}
                      >
                        <ViewSidebarIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Minimize">
                      <IconButton
                        size="small"
                        onClick={() => setIsMinimized(true)}
                        sx={{ color: colors.primary[300] }}
                      >
                        <KeyboardArrowDownIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <IconButton size="small" onClick={onClose} sx={{ color: colors.primary[300] }}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </Stack>

                {renderChatArea("40vh")}
              </Paper>
            </Fade>
          )}

          {/* Contextual Suggestion Chips Row above Input Pill in bottomBar mode */}
          {chatHistory.length === 0 && !loading && (
            <Stack
              direction="row"
              spacing={1}
              sx={{
                overflowX: "auto",
                maxWidth: "100%",
                py: 0.5,
                px: 0.5,
                "&::-webkit-scrollbar": { display: "none" },
              }}
            >
              <Chip
                icon={<RateReviewIcon sx={{ fontSize: 14 }} />}
                label="Request Review"
                onClick={() => handleSendMessage("Draft a WhatsApp message to client requesting a review of their booking experience.")}
                clickable
                sx={{
                  bgcolor: theme.palette.mode === "dark" ? "rgba(30, 41, 59, 0.8)" : "rgba(255, 255, 255, 0.95)",
                  color: colors.primary[700],
                  borderColor: theme.palette.mode === "dark" ? "#334155" : "#e2e8f0",
                  border: "1px solid",
                  fontWeight: 600,
                  fontSize: "12px",
                  backdropFilter: "blur(8px)",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
                  "&:hover": {
                    borderColor: "#724E41",
                    bgcolor: "action.hover",
                  },
                }}
              />
              <Chip
                icon={<FavoriteBorderIcon sx={{ fontSize: 14 }} />}
                label="Send Thank You"
                onClick={() => handleSendMessage("Draft a WhatsApp thank you message to the client for choosing Muzukuru.")}
                clickable
                sx={{
                  bgcolor: theme.palette.mode === "dark" ? "rgba(30, 41, 59, 0.8)" : "rgba(255, 255, 255, 0.95)",
                  color: colors.primary[700],
                  borderColor: theme.palette.mode === "dark" ? "#334155" : "#e2e8f0",
                  border: "1px solid",
                  fontWeight: 600,
                  fontSize: "12px",
                  backdropFilter: "blur(8px)",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
                  "&:hover": {
                    borderColor: "#724E41",
                    bgcolor: "action.hover",
                  },
                }}
              />
              {isFuneral && (
                <Chip
                  icon={<EventIcon sx={{ fontSize: 14 }} />}
                  label="Anniversary Memorial"
                  onClick={() => handleSendMessage("Draft a comforting WhatsApp message to the family for the anniversary memorial.")}
                  clickable
                  sx={{
                    bgcolor: theme.palette.mode === "dark" ? "rgba(30, 41, 59, 0.8)" : "rgba(255, 255, 255, 0.95)",
                    color: colors.primary[700],
                    borderColor: theme.palette.mode === "dark" ? "#334155" : "#e2e8f0",
                    border: "1px solid",
                    fontWeight: 600,
                    fontSize: "12px",
                    backdropFilter: "blur(8px)",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
                    "&:hover": {
                      borderColor: "#724E41",
                      bgcolor: "action.hover",
                    },
                  }}
                />
              )}
              <Chip
                icon={<ViewAgendaIcon sx={{ fontSize: 14 }} />}
                label="Summarize Booking"
                onClick={() => handleSendMessage("Summarize the details of this booking.")}
                clickable
                sx={{
                  bgcolor: theme.palette.mode === "dark" ? "rgba(30, 41, 59, 0.8)" : "rgba(255, 255, 255, 0.95)",
                  color: colors.primary[700],
                  borderColor: theme.palette.mode === "dark" ? "#334155" : "#e2e8f0",
                  border: "1px solid",
                  fontWeight: 600,
                  fontSize: "12px",
                  backdropFilter: "blur(8px)",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
                  "&:hover": {
                    borderColor: "#724E41",
                    bgcolor: "action.hover",
                  },
                }}
              />
            </Stack>
          )}

          {/* Main Floating Bottom Bar Input Pill */}
          <Paper
            elevation={6}
            sx={{
              width: "100%",
              borderRadius: "32px",
              p: "6px 12px 6px 16px",
              bgcolor: theme.palette.mode === "dark" ? "#1e293b" : "#ffffff",
              border: `1.5px solid ${theme.palette.mode === "dark" ? "#334155" : "#e2e8f0"}`,
              boxShadow: "0 12px 30px rgba(0,0,0,0.12)",
              display: "flex",
              alignItems: "center",
              gap: 1,
              transition: "border-color 0.2s ease, box-shadow 0.2s ease",
              "&:focus-within": {
                borderColor: "#724E41",
                boxShadow: "0 14px 35px rgba(114, 78, 65, 0.2)",
              },
            }}
          >
            {chatHistory.length > 0 && (
              <Tooltip title="Clear chat history">
                <IconButton size="small" onClick={() => setChatHistory([])} sx={{ color: colors.primary[300] }}>
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}

            <TextField
              placeholder={
                chatHistory.length > 0
                  ? "Ask AI to refine or ask a question..."
                  : "Ask AI to write a WhatsApp message or ask a question..."
              }
              fullWidth
              variant="standard"
              value={inputPrompt}
              onChange={(e) => setInputPrompt(e.target.value)}
              disabled={loading}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage(inputPrompt);
                }
              }}
              InputProps={{
                disableUnderline: true,
                sx: {
                  fontSize: "14px",
                  color: colors.primary[700],
                  fontFamily: "inherit",
                },
              }}
            />

            <Tooltip title="Switch to Floating Side Panel">
              <IconButton
                size="small"
                onClick={() => setLayoutMode("sidePanel")}
                sx={{ color: colors.primary[300] }}
              >
                <ViewSidebarIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <IconButton
              size="small"
              disabled={loading || !inputPrompt.trim()}
              onClick={() => handleSendMessage(inputPrompt)}
              sx={{
                bgcolor: inputPrompt.trim() ? "#724E41" : "action.disabledBackground",
                color: "white",
                width: 32,
                height: 32,
                "&:hover": {
                  bgcolor: "#5d3e33",
                },
                "&.Mui-disabled": {
                  color: "rgba(255, 255, 255, 0.3)",
                },
              }}
            >
              {loading ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <ArrowUpwardIcon sx={{ fontSize: 18 }} />
              )}
            </IconButton>
          </Paper>
        </Box>
      )}

      {/* ---------------------------------------------------- */}
      {/* LAYOUT 2: FLOATING SIDE PANEL MODE (Google Docs Gemini Side Panel) */}
      {/* ---------------------------------------------------- */}
      {layoutMode === "sidePanel" && (
        <Slide direction="left" in={layoutMode === "sidePanel"}>
          <Paper
            elevation={12}
            sx={{
              position: "fixed",
              bottom: 24,
              right: 24,
              width: { xs: "calc(100vw - 32px)", sm: 420 },
              height: "calc(100vh - 48px)",
              display: "flex",
              flexDirection: "column",
              borderRadius: "20px",
              bgcolor: theme.palette.mode === "dark" ? "#1e293b" : "#ffffff",
              border: `1px solid ${theme.palette.mode === "dark" ? "#334155" : "#e2e8f0"}`,
              boxShadow: "0 24px 48px rgba(0,0,0,0.22)",
              pointerEvents: "auto",
              overflow: "hidden",
            }}
          >
            {/* HEADER */}
            <Box
              sx={{
                p: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                borderBottom: `1px solid ${
                  theme.palette.mode === "dark" ? "#334155" : "#f1f5f9"
                }`,
                bgcolor: theme.palette.mode === "dark" ? "#0f172a" : "#fafafa",
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <AutoAwesomeIcon sx={{ color: "#724E41" }} />
                <Typography
                  variant="h6"
                  fontWeight={700}
                  sx={{ color: colors.primary[700], fontSize: "16px" }}
                >
                  Muzukuru AI Assist
                </Typography>
              </Stack>
              <Stack direction="row" spacing={0.5}>
                <Tooltip title="Clear chat history">
                  <IconButton size="small" onClick={() => setChatHistory([])} sx={{ color: colors.primary[300] }}>
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Switch to Floating Bottom Bar">
                  <IconButton
                    size="small"
                    onClick={() => setLayoutMode("bottomBar")}
                    sx={{ color: colors.primary[300] }}
                  >
                    <ViewAgendaIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Minimize">
                  <IconButton
                    size="small"
                    onClick={() => setIsMinimized(true)}
                    sx={{ color: colors.primary[300] }}
                  >
                    <KeyboardArrowDownIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <IconButton size="small" onClick={onClose} sx={{ color: colors.primary[300] }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Box>

            {/* BODY */}
            <Box sx={{ flex: 1, overflowY: "hidden", p: 2.5, display: "flex", flexDirection: "column" }}>
              {renderChatArea()}
            </Box>

            {/* BOTTOM INPUT / FOOTER */}
            <Box
              sx={{
                p: 2,
                borderTop: `1px solid ${
                  theme.palette.mode === "dark" ? "#334155" : "#f1f5f9"
                }`,
                bgcolor: theme.palette.mode === "dark" ? "#0f172a" : "#fafafa",
              }}
            >
              {renderInputBar()}
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  textAlign: "center",
                  mt: 1,
                  color: "#94a3b8",
                  fontSize: "10px",
                }}
              >
                AI-generated content can make mistakes. Please verify.
              </Typography>
            </Box>
          </Paper>
        </Slide>
      )}
    </Box>
  );
}
